import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, ChevronDown, ChevronLeft, ChevronRight, FileText, Heart, KeyRound, Loader2, User } from 'lucide-react';
import { Card, PrimaryButton, TicketingShell } from '../../components/ticketing/TicketingShell';
import OrderSummary from '../../components/ticketing/OrderSummary';
import {
  ConsentInput, CountrySelect, FieldShell, FileInput, MultiSelectInput, PhoneInput, PhotoInput, SelectInput, TextInput,
  VerifiedEmailInput,
} from '../../components/ticketing/PremiumFields';
import { ApiService } from '../../utils/api';
import { fieldsForPass, groupSections, toAttendeePayload, validateFields } from '../../utils/bookingForm';
import { addPricedOption, chargeable, optionCurrency, optionPrice } from '../../utils/pricing';
import { formatCurrency } from '../../utils/currency';
import { formatMoney, inviteSearch, passCurrency, passSaleState, premiumHome, readCart, writeCart } from '../../utils/ticketing';
import { openCashfreeCheckout, submitPayuForm } from '../../utils/cashfree';
import { usePreviewMode } from '../../utils/ticketingPreview';

const sectionIcon = (title) => {
  if (/person|contact|basic|your/i.test(title)) return User;
  if (/profess|work|organi|company|institut/i.test(title)) return Briefcase;
  if (/interest|prefer/i.test(title)) return Heart;
  return FileText;
};

const emptyAttendee = () => ({ values: {}, tokens: {}, uploadUserId: null });
const draftKey = (eventId, passId) => `tkDraft:${eventId}:${passId}`;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readDraft = (eventId, passId) => {
  try {
    const saved = JSON.parse(localStorage.getItem(draftKey(eventId, passId)) || 'null');
    if (!saved || Date.now() - saved.savedAt > DRAFT_TTL_MS || !Array.isArray(saved.list)) return [];
    return saved.list;
  } catch (_) {
    return [];
  }
};

const loadDraft = (eventId, passId, qty) => {
  const list = readDraft(eventId, passId);
  return Array.from({ length: qty }, (_, i) => ({ ...emptyAttendee(), ...(list[i] || {}) }));
};

/** Attendee details for the chosen pass: sections with Next, one form per attendee, then booking and payment. */
const BookingForm = ({ view, eventRef, passId }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const preview = usePreviewMode();
  const inviteCode = new URLSearchParams(search).get('invite') || null;
  const onSpot = new URLSearchParams(search).get('onspot') === '1';
  const t = view.ticketing;
  const pass = t.passes.find((p) => p.id === passId);
  const currency = passCurrency(pass, view);
  const cart = readCart(view.eventId);
  const max = pass?.maxPerBooking || 1;
  const [qty, setQty] = useState(() => Math.min(max, cart?.passId === passId ? cart.qty : 1));
  const [attendees, setAttendees] = useState(() => loadDraft(view.eventId, passId, qty));
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(0);
  const [errors, setErrors] = useState({});
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState(null);
  const [access, setAccess] = useState(null);
  const [accessChecking, setAccessChecking] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [previewDone, setPreviewDone] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const topRef = useRef(null);

  const { data: event, isLoading } = useQuery({
    queryKey: ['publicEvent', view.eventId],
    queryFn: () => ApiService.getPublicEvent(view.eventId),
  });
  const fields = useMemo(() => fieldsForPass(event?.formSchema || [], passId, t.fields), [event, passId, t.fields]);
  const sections = useMemo(() => groupSections(fields), [fields]);

  useEffect(() => {
    setAttendees((list) => Array.from({ length: qty }, (_, i) => list[i] || emptyAttendee()));
    setCurrent((c) => Math.min(c, qty - 1));
    writeCart(view.eventId, { passId, qty });
  }, [qty, passId, view.eventId]);

  useEffect(() => {
    try {
      localStorage.setItem(draftKey(view.eventId, passId), JSON.stringify({
        savedAt: Date.now(),
        list: attendees.map((a) => ({ values: a.values, uploadUserId: a.uploadUserId })),
      }));
    } catch (_) { /* storage unavailable */ }
  }, [attendees, view.eventId, passId]);

  const { data: invite } = useQuery({
    queryKey: ['publicInvite', inviteCode],
    queryFn: () => ApiService.getPublicInvite(inviteCode),
    enabled: !!inviteCode,
  });
  const validInvite = invite?.valid && invite.eventId === view.eventId ? invite : null;

  const checkCode = async () => {
    const code = accessCode.trim();
    if (!code || preview) return;
    setAccessChecking(true);
    try {
      const result = await ApiService.checkBookingAccess(view.eventId, { passId, inviteCode, accessCode: code });
      setAccess(result);
      setAccessError(result.valid ? null : result.reason);
    } catch (err) {
      setAccess(null);
      setAccessError(err.message || 'Could not check this code');
    } finally {
      setAccessChecking(false);
    }
  };

  const attendee = attendees[current] || emptyAttendee();
  const back = pass?.groupId
    ? `/register/${eventRef}/passes/${pass.groupId}${inviteSearch(search)}`
    : premiumHome(eventRef, search);

  if (!pass || passSaleState(pass) !== 'open') {
    return (
      <TicketingShell view={view} back={premiumHome(eventRef, search)} title={view.name} subtitle={t.tagline}>
        <Card className="p-8 text-center text-gray-600">
          {!pass ? 'This category isn\'t available.' : passSaleState(pass) === 'upcoming' ? 'Registration for this category opens soon.' : 'Registration for this category has closed.'}
        </Card>
      </TicketingShell>
    );
  }

  const update = (index, change) => setAttendees((list) => list.map((a, i) => (i === index ? change(a) : a)));
  const setValue = (field, value) => {
    update(current, (a) => {
      const tokens = field.type === 'email' && a.values[field.label] !== value ? { ...a.tokens, [field.label]: undefined } : a.tokens;
      return { ...a, values: { ...a.values, [field.label]: value }, tokens };
    });
    setErrors((e) => ({ ...e, [`${current}:${field.id}`]: undefined }));
  };

  const sectionErrors = (index, attendeeIndex = current) => {
    const found = validateFields(sections[index].fields, attendees[attendeeIndex].values,
      { verifyEmail: t.verifyEmail, tokens: attendees[attendeeIndex].tokens });
    return Object.fromEntries(Object.entries(found).map(([id, message]) => [`${attendeeIndex}:${id}`, message]));
  };

  const next = () => {
    const found = sectionErrors(open);
    setErrors((e) => ({ ...e, ...found }));
    if (Object.keys(found).length) return;
    if (open < sections.length - 1) {
      setOpen(open + 1);
    } else if (current < qty - 1) {
      setCurrent(current + 1);
      setOpen(0);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setOpen(-1);
    }
  };

  const submit = async () => {
    setSubmitError(null);
    const all = {};
    let firstBad = null;
    attendees.forEach((_, a) => sections.forEach((__, s) => {
      const found = sectionErrors(s, a);
      if (Object.keys(found).length && !firstBad) firstBad = { a, s };
      Object.assign(all, found);
    }));
    setErrors(all);
    if (firstBad) {
      setCurrent(firstBad.a);
      setOpen(firstBad.s);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (pass.accessCodeRequired && !accessCode.trim()) {
      setAccessError('Enter the access code from your invitation');
      return;
    }
    if (view.inviteOnly && !inviteCode) {
      setSubmitError('Registration for this event is by invitation only. Use the invite link you were sent.');
      return;
    }
    if (preview) {
      setPreviewDone(true);
      return;
    }
    setSubmitting(true);
    try {
      const booking = await ApiService.createBooking(view.eventId, {
        passId,
        inviteCode,
        accessCode: accessCode.trim() || null,
        couponCode: coupon?.couponCode || null,
        attendees: attendees.map((a) => toAttendeePayload(fields, a, pass.name)),
        source: onSpot ? 'ON-SPOT' : null,
      });
      try { localStorage.removeItem(draftKey(view.eventId, passId)); } catch (_) { /* ignore */ }
      writeCart(view.eventId, null);
      const statusPage = `/register/${eventRef}/booking/${booking.bookingId}`;
      if (!booking.paymentRequired) {
        navigate(statusPage, { replace: true });
        return;
      }
      const order = await ApiService.startBookingCheckout(view.eventId, booking.bookingId);
      if (order.provider === 'PAYU') {
        submitPayuForm(order.payu);
        return;
      }
      await openCashfreeCheckout(order.paymentSessionId, order.cashfreeMode);
      navigate(`${statusPage}?order_id=${encodeURIComponent(order.orderId)}`, { replace: true });
    } catch (err) {
      const message = err.message || 'Something went wrong. Please try again.';
      if (/access code/i.test(message)) setAccessError(message);
      else if (/coupon/i.test(message)) { setCouponError(message); setSubmitError(message); }
      else setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const props = {
      field,
      value: attendee.values[field.label],
      onChange: (value) => setValue(field, value),
      error: errors[`${current}:${field.id}`],
    };
    const upload = {
      eventId: view.eventId,
      uploadUserId: attendee.uploadUserId,
      onUploaded: (userId) => userId && update(current, (a) => ({ ...a, uploadUserId: a.uploadUserId || userId })),
    };
    let input;
    switch (field.type) {
      case 'email':
        input = t.verifyEmail ? (
          <VerifiedEmailInput {...props} eventId={view.eventId} verified={!!attendee.tokens[field.label]}
            onVerified={(token) => update(current, (a) => ({ ...a, tokens: { ...a.tokens, [field.label]: token } }))} />
        ) : <TextInput {...props} type="email" />;
        break;
      case 'phone': input = <PhoneInput {...props} />; break;
      case 'country': input = <CountrySelect {...props} />; break;
      case 'consent': input = <ConsentInput {...props} termsUrl={t.termsUrl} privacyUrl={t.privacyUrl} consentText={t.consentText} />; break;
      case 'photo': input = <PhotoInput {...props} {...upload} />; break;
      case 'file': input = <FileInput {...props} {...upload} />; break;
      case 'dropdown':
      case 'radio':
        input = <SelectInput {...props} options={(field.optionsWithPricing?.length ? field.optionsWithPricing : (field.options || []).map((label) => ({ label })))
          .filter((option) => option.label)
          .map((option) => ({
            value: option.label,
            label: optionPrice(option) > 0 ? `${option.label} — ${formatCurrency(optionPrice(option), optionCurrency(option))}` : option.label,
          }))} />;
        break;
      case 'multiselect':
        input = <MultiSelectInput {...props} options={field.options?.length ? field.options : (field.optionsWithPricing || []).map((o) => o.label)} />;
        break;
      case 'textarea':
        input = (
          <textarea id={field.id} rows={3} value={props.value ?? ''} onChange={(e) => props.onChange(e.target.value)}
            className={`w-full rounded-xl border px-4 py-3 text-base outline-none focus:border-[var(--tk-primary)] ${props.error ? 'border-red-400' : 'border-gray-300'}`} />
        );
        break;
      case 'number': input = <TextInput {...props} type="number" />; break;
      case 'date': input = <TextInput {...props} type="date" />; break;
      default: input = <TextInput {...props} />;
    }
    const wide = ['consent', 'photo', 'file', 'textarea', 'multiselect'].includes(field.type);
    return <FieldShell key={field.id} field={field} error={props.error} wide={wide}>{input}</FieldShell>;
  };

  const extrasFor = (values) => {
    let totals = {};
    fields.forEach((field) => {
      const selected = values?.[field.label];
      const option = (field.optionsWithPricing || []).find((item) => item.label === selected);
      if (option) totals = addPricedOption(totals, option);
    });
    return chargeable(totals, currency);
  };
  const extrasTotal = pass.type === 'APPROVAL' ? 0 : attendees.reduce((sum, item) => sum + extrasFor(item.values), 0);
  const discountTotal = coupon ? coupon.discountPerAttendee * qty : 0;
  const passTotal = pass.type === 'PAID' ? Math.max(0, pass.price * qty - discountTotal) : 0;
  const total = passTotal + extrasTotal;
  const couponsAllowed = !!event?.enableCouponCode && pass.type === 'PAID' && currency === 'INR';
  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    if (preview) {
      setCouponError('Coupons are checked on the live page, not in the preview.');
      return;
    }
    setCouponBusy(true);
    setCouponError(null);
    try {
      const quote = await ApiService.quoteBooking(view.eventId, { passId, quantity: qty, couponCode: code });
      setCoupon(quote);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.message || 'This coupon can\'t be used');
    } finally {
      setCouponBusy(false);
    }
  };
  const actionLabel = pass.type === 'PAID' ? `Continue to pay ${formatMoney(total, currency)}` : pass.type === 'APPROVAL' ? 'Submit for approval' : 'Complete registration';

  return (
    <TicketingShell view={view} back={back} title={view.name} subtitle={t.tagline}>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div ref={topRef} className="scroll-mt-6">
          <h1 className="text-3xl font-bold sm:text-4xl">Attendee details</h1>
          <span className="mt-3 block h-1 w-16 rounded bg-[var(--tk-accent)]" />
          <p className="mt-4 text-gray-600">Please provide the details of each {pass.name} attendee.</p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-lg font-semibold">{pass.name}</p>
            {qty > 1 && (
              <div className="flex items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--tk-primary)_8%,white)] px-2 py-1 ring-1 ring-[color-mix(in_srgb,var(--tk-primary)_20%,white)]">
                <button type="button" onClick={() => { setCurrent(Math.max(0, current - 1)); setOpen(0); }} disabled={current === 0} className="rounded-full p-1.5 disabled:opacity-30" aria-label="Previous attendee">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="px-2 font-semibold tabular-nums text-[var(--tk-primary)]">Attendee {current + 1}/{qty}</span>
                <button type="button" onClick={() => { setCurrent(Math.min(qty - 1, current + 1)); setOpen(0); }} disabled={current === qty - 1} className="rounded-full p-1.5 disabled:opacity-30" aria-label="Next attendee">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
          ) : (
            <div className="mt-4 space-y-4">
              {sections.map((section, index) => {
                const Icon = sectionIcon(section.title);
                const expanded = open === index;
                const hasError = section.fields.some((f) => errors[`${current}:${f.id}`]);
                return (
                  <Card key={section.title} className={hasError ? 'ring-1 ring-red-300' : ''}>
                    <button type="button" onClick={() => setOpen(expanded ? -1 : index)} aria-expanded={expanded}
                      className="flex w-full items-center gap-4 px-5 py-5 text-left sm:px-6">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-600"><Icon className="h-5 w-5" /></span>
                      <span className="flex-1 text-lg font-semibold">{section.title}</span>
                      <ChevronDown className={`h-6 w-6 transition ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded && (
                      <div className="border-t px-5 pb-6 pt-5 sm:px-6">
                        <div className="grid gap-5 sm:grid-cols-2">{section.fields.map(renderField)}</div>
                        <div className="mt-6 flex justify-end">
                          <PrimaryButton onClick={next} className="min-w-[140px]">
                            Next <ChevronRight className="h-5 w-5" />
                          </PrimaryButton>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}

              {inviteCode && invite && (
                <p className={`rounded-xl p-3 text-sm ${validInvite ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} role="status">
                  {validInvite ? `You're invited by ${validInvite.name}. Enter your access code below if you have one.` : (invite.reason || 'This invite link is not valid for this event.')}
                </p>
              )}
              {view.inviteOnly && !inviteCode && (
                <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" role="status">Registration for this event is by invitation only. Use the invite link you were sent.</p>
              )}

              <Card className="space-y-5 p-5 sm:p-6">
                {(pass.accessCodeRequired || showAccess) && (
                  <div>
                    <label htmlFor="tk-access" className="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-800">
                      <KeyRound className="h-4 w-4" />Access code{pass.accessCodeRequired && <span className="text-red-500">*</span>}
                    </label>
                    <input id="tk-access" value={accessCode} onChange={(e) => { setAccessCode(e.target.value); setAccessError(null); setAccess(null); }}
                      onBlur={checkCode} placeholder="Code from your invitation" autoComplete="off"
                      aria-describedby="tk-access-status"
                      className={`h-12 w-full rounded-xl border px-4 text-base uppercase tracking-wider outline-none focus:border-[var(--tk-primary)] sm:max-w-sm ${accessError ? 'border-red-400' : 'border-gray-300'}`} />
                    {accessChecking && <p className="mt-1 text-sm text-gray-500">Checking code…</p>}
                    {accessError && <p id="tk-access-status" className="mt-1 text-sm text-red-600" role="alert">{accessError}</p>}
                    {access?.valid && (
                      <p id="tk-access-status" className="mt-1 text-sm text-green-700" role="status">
                        {access.groupName ? `Invited by ${access.groupName}. ` : ''}
                        {access.benefit === 'FREE' ? 'Registration is free with this code.' : access.description ? `${access.description} applied.` : 'Code applied.'}
                      </p>
                    )}
                  </div>
                )}
                {!pass.accessCodeRequired && !showAccess && (
                  <button type="button" onClick={() => setShowAccess(true)} className="text-sm font-medium text-[var(--tk-primary)] underline">Have an access code?</button>
                )}
                {submitError && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700" role="alert">{submitError}</p>}
                {previewDone && (
                  <p className="rounded-xl bg-blue-50 p-3 text-sm text-blue-800" role="status">
                    Preview: the form is complete. On the live page this would {pass.type === 'PAID' ? `create the booking and open payment for ${formatMoney(total, currency)}` : pass.type === 'APPROVAL' ? 'submit the booking for approval' : 'confirm the registration'}.
                  </p>
                )}
                <PrimaryButton onClick={submit} disabled={submitting || isLoading} className="w-full text-lg">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : actionLabel}
                </PrimaryButton>
              </Card>
            </div>
          )}
        </div>

        <OrderSummary
          pass={pass}
          qty={qty}
          currency={currency}
          note={t.note}
          extras={extrasTotal}
          discount={coupon ? { amount: discountTotal, code: coupon.couponCode } : null}
        >
          {max > 1 && (
            <div className="flex items-center justify-between border-t px-6 py-4 text-sm">
              <span className="text-gray-600">Attendees</span>
              <select value={qty} onChange={(e) => setQty(Number(e.target.value))} className="rounded-lg border px-3 py-1.5" aria-label="Number of attendees">
                {Array.from({ length: max }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
          {couponsAllowed && (
            <div className="border-t px-6 py-4">
              {coupon ? (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-green-700">Coupon {coupon.couponCode} applied</span>
                  <button type="button" onClick={() => { setCoupon(null); setCouponInput(''); }} className="text-gray-600 underline">Remove</button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <input value={couponInput} onChange={(e) => { setCouponInput(e.target.value); setCouponError(null); }}
                      placeholder="Enter coupon code" aria-label="Coupon code"
                      className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-sm uppercase outline-none focus:border-[var(--tk-primary)]" />
                    <button type="button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()}
                      className="rounded-lg bg-[var(--tk-primary)] px-4 text-sm font-semibold text-white disabled:opacity-50">
                      {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </button>
                  </div>
                  {couponError && <p className="mt-1 text-sm text-red-600" role="alert">{couponError}</p>}
                </div>
              )}
            </div>
          )}
        </OrderSummary>
      </div>
    </TicketingShell>
  );
};

export default BookingForm;
